import { Component, EventEmitter, OnDestroy, OnInit, Output } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { letterValidator } from "../../../../validators/letter.validator";
import { digitalValidator } from "../../../../validators/digital.validators";
import { symbolValidator } from "../../../../validators/symbol.validator";
import { ChangeProfileDto } from "../../models/profile.models";
import { ProfileDataService } from "../../services/profile-data.service";
import { ModalService } from "../../../../services/modal.service";
import { Subscription } from "rxjs";

@Component({
	selector: "app-change-password-dialog",
	templateUrl: "./change-password-dialog.component.html",
	styleUrls: ["./change-password-dialog.component.scss"],
})
export class ChangePasswordDialogComponent implements OnInit, OnDestroy {
	@Output() changePassword: EventEmitter<void> = new EventEmitter<void>();

	form!: FormGroup;

	subs: Subscription[] = [];

	passwordsMatchError = false;

	constructor(
		private profileDataService: ProfileDataService,
		private modalService: ModalService
	) {}

	ngOnInit() {
		this.initForm();
		this.listenerModalEvent();
		this.initPasswordValidation();
	}

	ngOnDestroy() {
		this.subs.forEach((item) => item.unsubscribe());
	}

	listenerModalEvent() {
		this.subs.push(
			this.modalService.isModalOpen$.subscribe({
				next: (res) => {
					if (!res) {
						this.form.reset();
					}
				},
			})
		);
	}

	initForm(): void {
		this.form = new FormGroup({
			oldPasswordFormControl: new FormControl("", [Validators.required]),
			passwordFormControl: new FormControl("", [
				Validators.required,
				Validators.minLength(8),
				letterValidator(),
				digitalValidator(),
				symbolValidator(),
			]),
			passwordFormControlConfirm: new FormControl("", [Validators.required]),
		});
	}

	get oldPasswordRequiredError() {
		return this.form.get("oldPasswordFormControl")?.hasError("required");
	}

	get passwordRequiredError() {
		return this.form.get("passwordFormControl")?.hasError("minlength") || this.form.get("passwordFormControl")?.hasError("required");
	}

	get passwordLetterFormatError() {
		return this.form.get("passwordFormControl")?.hasError("letterNotValid");
	}

	get passwordDigitalFormatError() {
		return this.form.get("passwordFormControl")?.hasError("digitalNotValid");
	}

	get passwordSymbolFormatError() {
		return this.form.get("passwordFormControl")?.hasError("symbolNotValid");
	}

	get passwordConfirmRequiredError() {
		return this.form.get("passwordFormControlConfirm")?.hasError("required");
	}

	get passwordConfirmError() {
		return this.form.get("passwordFormControl")?.value !== this.form.get("passwordFormControlConfirm")?.value;
	}

	initPasswordValidation() {
		this.form.get("passwordFormControl")?.valueChanges.subscribe(() => {
			const { oldPasswordFormControl, passwordFormControl } = this.form.value;
			this.passwordsMatchError = false;
			if (!!passwordFormControl && !!oldPasswordFormControl && passwordFormControl === oldPasswordFormControl) {
				this.passwordsMatchError = true;
			}
		});
	}

	sendForm() {
		const { oldPasswordFormControl, passwordFormControl } = this.form.value;
		this.passwordsMatchError = false;

		if (!!passwordFormControl && !!oldPasswordFormControl && passwordFormControl === oldPasswordFormControl) {
			this.passwordsMatchError = true;
			return;
		}

		const data: ChangeProfileDto = {
			old_password: oldPasswordFormControl,
			new_password: passwordFormControl,
			password_confirmation: this.form.get("passwordFormControlConfirm")?.value,
		};

		this.profileDataService.changePassword(data).subscribe({
			next: () => {
				this.modalService.closeModal();
				this.changePassword.emit();
				this.form.reset();
			},
		});
	}

	closeModal() {
		this.modalService.closeModal();
	}
}
